import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import {
  CACHE_KEY_DELIVERY_ADDRESS,
  CACHE_KEY_ORDER,
  CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS,
} from 'src/app/hooks/data/_keys';
import { useBffClient } from 'src/app/hooks/data/useBffClient';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import type { DeliveryAddress } from 'src/app/types';

export interface CreateAddressRequestBody {
  address: string;
  addressTwo?: string;
  zipCode: string;
  isDoorman: boolean;
  instructions?: string;

  /**
   * Will this address be the default for the account?
   */
  isDefault?: boolean;

  /**
   * If `true`, address validation does not occur when creating a new address
   * */
  forceCreation?: boolean;
}

export interface EditAddressRequestBody extends Omit<CreateAddressRequestBody, 'isDefault' | 'forceCreation'> {
  id: string;
}

export interface EditAddressRequestResponse {
  addressAddedToAccount: boolean;
  enteredAddress: DeliveryAddress;
  suggestedAddress?: DeliveryAddress;
}

export const useDeliveryAddressListByAccountId = (
  accountId: number,
  { enabled }: { enabled?: boolean }
): UseQueryResult<DeliveryAddress[], AxiosError> => {
  const authClient = useBffClient();

  return useQuery(
    [CACHE_KEY_DELIVERY_ADDRESS, accountId],
    () =>
      authClient.get(`accounts/${accountId}/delivery_addresses`).then((resp: { data: DeliveryAddress[] }) => resp.data),
    {
      ...(enabled !== undefined && { enabled }),
    }
  );
};

export const useDeliveryAddressUpdate = () => {
  const authClient = useBffClient();
  const queryClient = useQueryClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useMutation<EditAddressRequestResponse, AxiosError, EditAddressRequestBody>(
    ({ id, ...body }) => authClient.put(`accounts/${accountId}/delivery_addresses/${id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          CACHE_KEY_DELIVERY_ADDRESS,
          CACHE_KEY_ORDER,
          CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS,
        ]);
      },
    }
  );
};

export const useDeliveryAddressCreate = () => {
  const authClient = useBffClient();
  const queryClient = useQueryClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useMutation<{ data: EditAddressRequestResponse; status: number }, AxiosError, CreateAddressRequestBody>(
    body =>
      authClient.post(`accounts/${accountId}/delivery_addresses`, body).then(resp => ({
        data: resp.data,
        status: resp.status,
      })),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          CACHE_KEY_DELIVERY_ADDRESS,
          CACHE_KEY_ORDER,
          CACHE_KEY_PHARMAKON_V3_ACTIVE_PENDING_MEDS,
        ]);
      },
    }
  );
};

export const useDeliveryAddressById = (deliveryAddressId?: string) => {
  const authClient = useBffClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useQuery<DeliveryAddress, AxiosError>(
    [CACHE_KEY_DELIVERY_ADDRESS, accountId, deliveryAddressId],
    () =>
      authClient.get(`delivery_addresses/${deliveryAddressId}`).then(res => {
        const attributes = res?.data?.data?.attributes ?? {};

        return {
          id: deliveryAddressId,
          instructions: attributes.notes,
          isDoorman: attributes.is_doorman,
          address: attributes.street,
          addressTwo: attributes.street_two,
          city: attributes.city,
          state: attributes.state,
          zipCode: attributes.zip_code,
          coordinates: {
            latitude: attributes.lat,
            longitude: attributes.lon,
          },
        };
      }),
    { enabled: !!accountId && !!deliveryAddressId }
  );
};

export const useDeliveryAddressDelete = (): UseMutationResult<void, AxiosError, number> => {
  const authClient = useBffClient();
  const queryClient = useQueryClient();
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser));
  const accountId = user?.accountId ?? '';

  return useMutation(
    (
      id // Delivery address ID.
    ) => authClient.delete(`accounts/${accountId}/delivery_addresses/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([CACHE_KEY_DELIVERY_ADDRESS]);
      },
    }
  );
};
